import type { TurnstileInstance, TurnstileProps } from '@marsidev/react-turnstile';
import { Turnstile } from '@marsidev/react-turnstile';
import { createRoot } from 'react-dom/client';
import React from 'react';

import CaptchaEvent from './Captcha.types';

export function createCaptchaComponent(
    input: HTMLInputElement | null,
    { onSuccess, onError, options }: Partial<TurnstileProps> = {},
    ref: React.RefObject<TurnstileInstance | null> = React.createRef<TurnstileInstance>()
): void {
    const siteKey = input?.getAttribute('data-sitekey');
    const container = input?.parentElement;
    const form = input?.closest('form');

    if (!input || !siteKey || !container) {
        return;
    }

    const id = input.getAttribute('id') ?? undefined;
    const responseFieldName = input.getAttribute('name') ?? undefined;

    const handleSuccess = (token: string): void => {
        if (onSuccess) {
            onSuccess(token);
            return;
        }

        form?.dispatchEvent(
            new CustomEvent(CaptchaEvent.SUCCESS, { bubbles: true, detail: { token } })
        );
    };

    const handleError = (error: string): void => {
        if (onError) {
            onError(error);
            return;
        }

        form?.dispatchEvent(new CustomEvent(CaptchaEvent.ERROR, { bubbles: true }));
    };

    const handleExpire = (): void => {
        form?.dispatchEvent(new CustomEvent(CaptchaEvent.EXPIRE, { bubbles: true }));
    };

    input.addEventListener(CaptchaEvent.EXECUTE, () => {
        ref.current?.execute();
    });

    createRoot(container).render(
        <Turnstile
            className="cf-captcha"
            id={id}
            ref={ref}
            onSuccess={handleSuccess}
            onError={handleError}
            onExpire={handleExpire}
            siteKey={siteKey}
            options={{
                appearance: 'interaction-only',
                execution: 'execute',
                feedbackEnabled: false,
                responseFieldName,
                theme: 'light',
                ...options
            }}
        />
    );
}

export default function renderCaptchaComponent(): void {
    const inputs = document.querySelectorAll<HTMLInputElement>('[data-role="cf-turnstile"]');

    for (const input of inputs) {
        const form = input.hasAttribute('data-lazy') ? input.closest('form') : null;

        if (form) {
            form.addEventListener('focusin', () => createCaptchaComponent(input), { once: true });
            continue;
        }

        createCaptchaComponent(input);
    }
}
